/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetDefaultFilters';
import { analyzeActionQuery } from '../ActionQuery/ActionQueryAnalyze';

const ActionSetDefaultFiltersManager: ActionManagerFn<
  'ActionSetDefaultFilters'
> = (platform: Platform): ActionManager<'ActionSetDefaultFilters'> => {
  return {
    ...baseActionManager(platform, 'ActionSetDefaultFilters'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionSetQueryFilter'] as ActionItemType[]).includes(
        childType as ActionItemType,
      ),
    props: {
      ...baseActionProps,
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'string',
      },
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        pageIdProp: 'embeddedPageId',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      showEmptyFilterWarning: {
        label: 'Show Empty Filter Warning',
        type: 'boolean',
        helpText:
          'Enable this to know if empty value passed to filters(Recommended for developer).',
      },
    },
    name: 'Set Default Filters',
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'QUERY_FILTER'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'search' },
    analyze: analyzeActionQuery,
    description: `**Overview**<br/>
The Set Default Filters action is a data initialization component designed to establish baseline constraints on a Data Store. Unlike standard query actions that respond to user input (like a search bar), this action is typically used during the page load lifecycle to define the "starting state" of the data.<br/><br/>
This component serves as a container for ActionSetQueryFilter children. It allows developers to programmatically inject SQL-like WHERE clauses (e.g., "Status = Active") that are applied automatically when the data is first fetched. This ensures that users are presented with a relevant subset of data immediately, without needing to manually apply filters themselves.

**Key Features**<br/>
* **Baseline Constraints:** Defines the fundamental rules for what data should be visible initially (e.g., always filtering by the current user's ID).
* **Strict Child Architecture:** Explicitly restricts children to ActionSetQueryFilter, ensuring the component is used solely for defining filter logic.
* **Developer Diagnostics:** Includes a showEmptyFilterWarning property to help identify configuration errors during development, such as when a variable used in a filter evaluates to null.
* **Scope Management:** Supports an embeddedPageId property, allowing the action to target Data Stores residing within nested or embedded page containers.
* **Broad Store Compatibility:** Works with CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the specific Data Store to which the default filters will be applied.
* **Embedded Page ID:** A string property.
  * *Function:* Used to resolve the Alias if the target store is inside a different page context (e.g., an embedded widget).
* **Show Empty Filter Warning:** A boolean property.
  * *Function:* If enabled, triggers a warning log if a filter attempts to execute with an empty or undefined value, aiding in debugging complex expressions.

**Use Cases**<br/>
* **Security & Privacy:** Automatically filtering an "Orders" grid to show only records where CustomerID matches the logged-in user's ID.
* **Data Hygiene:** Setting a default filter of IsDeleted = false or Status != 'Archived' to ensure the user only sees active records when the page opens.
* **Contextual Views:** Loading a "Task List" page and immediately filtering it to show only tasks due "Today" or "This Week" by default.`,
  };
};

export default ActionSetDefaultFiltersManager;
